import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Filter, Clock, GraduationCap, ArrowRight, Sparkles, X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { NEW_SHELF_DAYS, splitFresh } from "../constants/newShelf";
import { NewShelf } from "./NewShelf";
import { CONTENT, NAV, notShelved } from "../constants/navigation";
import { COLLECTION_BY_ID, PRO_COLLECTION, PRO_KIND_LABEL, isLongVideo } from "../constants/collections";
import { TEAM_CATEGORY_LABEL, DRILL_FORMAT_LABEL } from "../constants/categories";
import { LOGO } from "../constants/branding";
import { Ic } from "../constants/icons";
import { ContentCard, ContentDetail } from "./ContentCard";
import { VideoTheater } from "./VideoTheater";
import { useTypedHint } from "../hooks/useTypedHint";
import { MasterclassCard } from "./MasterclassCard";
import { ContentGridSkeleton } from "./ContentSkeleton";
import { Glide } from "../shared/motion";
import { useDrills } from "../hooks/useDrills";
import { useWatched } from "../hooks/useWatched";
import { shuffleDaily } from "../constants/shuffle";
import { logEvent } from "../utils/analytics";
import { MasterclassReader } from "../masterclass/MasterclassReader";
import { MASTERCLASS_MODULES } from "../masterclass/registry";
import { useBamIQPage } from "../bamiq/BamIQ";
import { MasterclassCountdown } from "../masterclass/MasterclassCountdown";
import { AAU_MODULE_KEY } from "../masterclass/aauModule";
import { NBA_PLAYER_DEV_MODULE, NBA_PLAYER_DEV_KEY } from "../masterclass/nbaPlayerDevModule";
import { muxFrame } from "../masterclass/coverArt";
import { lessonsForAudience } from "../lessons";
import { LessonsRail } from "../lessons/LessonsRail";
import { DrillBulkBar, AdminSelectOverlay } from "./DrillBulkBar";
import { ComingSoonStrip } from "./ComingSoonStrip";
import { useAdmin } from "../shared/AdminContext";
import { MoreFilters, VideoSourceToggle } from "./MoreFilters";
import { useCoaches } from "../hooks/useCoaches";
import { useUserAddons } from "../utils/useUserAddons";
import { isAllowedBetaUser } from "../constants/addons";

// Masterclasses and lessons by key: the same registry BAM IQ's server reads
// when a coach attaches one to a conversation (masterclass/registry.js).
const MODULES = MASTERCLASS_MODULES;

const PracticePlansPage = lazy(() => import("../practice"));
const LazyPlansFallback = () => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:80}}>
    <div style={{width:28,height:28,border:`3px solid ${GOLD}33`,borderTop:`3px solid ${GOLD}`,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
  </div>
);

export function ContentPage({sectionId, C, dark, favDrills=[], toggleFav, videoNotes={}, saveVideoNotes, planFocusMode, setPlanFocusMode, favLists=[], favListMembership={}, toggleFavInList, createFavList, rosterNames=[]}){
  const [activeFilters, setActiveFilters] = useState(["All"]);
  // Native renders a capped, grow-on-scroll window of the (often 600+)
  // drill list. Rendering every card at once exhausts the iOS WKWebView
  // memory budget and crashes the WebContent process (Drills & Games has
  // ~632 cards) — the cause of the on-device crash loop. Web stays
  // uncapped: desktop has the RAM and admin bulk-tag needs the full set.
  const [visibleCount, setVisibleCount] = useState(24);
  const loadMoreRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState(()=>{try{return JSON.parse(localStorage.getItem("bam_recent_searches")||"[]");}catch{return[];}});
  // Semantic search results (Voyage embedding → search_drills RPC).
  // Empty when the query is too short or the request hasn't fired yet.
  // Each entry is { id, similarity, ... } — we match by id against
  // sectionItems below so the rendered card has the full local shape.
  const [aiResults, setAiResults] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  // Admin bulk-tagger selection state - only active when adminMode is on.
  const { isAdmin, adminMode } = useAdmin();
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const clearSelection = () => setSelectedIds(new Set());
  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const bulkTagActive = isAdmin && adminMode && (sectionId === "pd" || sectionId === "team");
  // Clear selection if admin mode turns off or we change sections.
  useEffect(() => { if (!bulkTagActive) clearSelection(); }, [bulkTagActive, sectionId]);
  // Library vs Bookmarks top-level tab (resets on section change so
  // switching sections doesn't leave you stuck on an empty bookmarks view).
  const [viewTab, setViewTab] = useState("library"); // "library" | "bookmarks" | "coming"
  // Pro Development entry. The chip is a doorway, not a filter - but the
  // doorway is played by BAMFull on arrival, over the page you are
  // going to, rather than here as a wait on the page you are leaving.
  // So this just goes. See the proArriving comment in BAMFull.jsx.
  const proCollection = COLLECTION_BY_ID[PRO_COLLECTION];
  const enterPro = () => {
    logEvent("pro_collection_open", { from: sectionId });
    window.location.hash = "#/pro";
  };
  const enterMovement = () => {
    logEvent("movement_collection_open", { from: sectionId });
    window.location.hash = "#/movement";
  };
  useEffect(() => { setViewTab("library"); }, [sectionId]);
  // BAM vs All videos source toggle (coach_type-based).
  const [source, setSource] = useState("all"); // "bam" | "all"
  // "More filters" - currently just coach selection. Set<string> of coach ids.
  const [selectedCoachIds, setSelectedCoachIds] = useState(() => new Set());
  const coaches = useCoaches();
  useEffect(() => { setSelectedCoachIds(new Set()); setSource("all"); }, [sectionId]);

  // Semantic search: debounce 280ms, fire /api/search-drills with the
  // current query + section scope. Sections that aren't drill libraries
  // (insights, ti, master) skip the call — they have no embeddings.
  // The team page expands its scope to include PD so cross-section
  // tactical drills surface here too.
  const SEMANTIC_SECTIONS = new Set(["pd", "team", "movement", "workouts"]);
  // Discoverability: example queries shown as clickable chips below the
  // search bar. Tuned per section to demonstrate the *kind* of input the
  // semantic search rewards — concept phrases, not single keywords. Keep
  // these short (≤24 chars) so chips stay one-line on mobile.
  const EXAMPLE_QUERIES = {
    pd: ["transition press break", "creative finishing", "decision-making"],
    team: ["half-court traps", "spacing concepts", "transition defense"],
    movement: ["change of direction", "deceleration", "first-step quickness"],
    workouts: ["live ball footwork", "pick-and-roll reads", "shot creation"],
  };
  // The example searches type themselves into the empty box, in place of a
  // row of "Try:" chips under it (Coleman, 2026-10-07): the same suggestions,
  // where the coach is already looking, and one row less above the drills.
  const typedHint = useTypedHint(
    SEMANTIC_SECTIONS.has(sectionId) && EXAMPLE_QUERIES[sectionId]
      ? ["Search or describe a drill…", ...EXAMPLE_QUERIES[sectionId].map(q => `Try "${q}"`)]
      : [],
    searchFocused || !!searchQ);
  useEffect(() => {
    const q = searchQ.trim();
    if (!SEMANTIC_SECTIONS.has(sectionId) || q.length < 3) {
      setAiResults([]);
      setAiLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAiLoading(true);
    const sectionForApi = sectionId === "team" ? ["team", "pd"] : sectionId;
    const timer = setTimeout(async () => {
      try {
        const r = await fetch("/api/search-drills", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q, section: sectionForApi, limit: 30 }),
        });
        if (cancelled) return;
        if (r.ok) {
          const j = await r.json();
          setAiResults(Array.isArray(j?.results) ? j.results : []);
        } else {
          setAiResults([]);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn("[ContentPage] AI search failed:", e?.message);
          setAiResults([]);
        }
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQ, sectionId]);
  // Prompt 8: masterclass progress
  const [masterProgress, setMasterProgress] = useState(()=>{try{return JSON.parse(localStorage.getItem("bam_master_progress")||"{}");}catch{return{};}});
  // Module reader state (full-screen masterclass)
  const [openModuleKey, setOpenModuleKey] = useState(null);
  // What's open, offered to BAM IQ for the coach to attach with a tap (D122).
  useBamIQPage(openModuleKey && MODULES[openModuleKey] ? { type: "masterclass", id: openModuleKey, title: MODULES[openModuleKey].title }
    : selectedItem?.id && selectedItem.type !== "module" ? { type: "drill", id: selectedItem.id, title: selectedItem.title } : null);
  // When a coach taps a card whose module is still pre-release, we hold
  // the item here so the countdown view can render the title, cover,
  // and live ticker. Cleared on close, same as openModuleKey.
  const [countdownItem, setCountdownItem] = useState(null);
  // Pre-release = a dateless coming-soon item, or one whose drop date
  // is still ahead. Covers both hand-written masterclass entries and
  // drills flagged with "Tease this" in the admin (useDrills maps the
  // flag into this same shape). Allowlisted accounts see through it so
  // the team can check the real thing in production.
  // Pre-release regardless of who is looking. isLocked adds the
  // allowlist bypass on top for the click gate; the strip and the grid
  // split use this one, so the team sees the strip laid out the way a
  // coach will rather than a different page from everyone else.
  const isPreRelease = (item) => !!item && (
    item.comingSoon || (item.availableAt && new Date(item.availableAt).getTime() > Date.now())
  );
  const isLocked = (item) => isPreRelease(item) && !allowlistBypass;
  // Allowlist bypass — Coleman + select team accounts skip the countdown
  // gate so they can review pre-release modules in production. Same
  // helper Film Studio used to use for its own pre-launch gate.
  const { userEmail } = useUserAddons();
  const allowlistBypass = isAllowedBetaUser(userEmail);

  // Auto-open a module when arriving at #/master with the flag set (e.g. from Team Insights promo).
  useEffect(() => {
    if (sectionId !== "master") return;
    try {
      const key = sessionStorage.getItem("bam_open_module");
      if (key && MODULES[key]) {
        setOpenModuleKey(key);
        sessionStorage.removeItem("bam_open_module");
      }
    } catch {}
  }, [sectionId]);

  const openModule = (moduleKey, itemId) => {
    setOpenModuleKey(moduleKey);
    logEvent("masterclass_module_open", { moduleKey, itemId });
  };
  const closeModule = () => {
    setOpenModuleKey(null);
  };
  const handleModuleProgress = (itemId) => (pct) => {
    setMasterProgress(prev => {
      const key = `master-${itemId}`;
      if (prev[key] === pct) return prev;
      const next = { ...prev, [key]: pct };
      try { localStorage.setItem("bam_master_progress", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const section = CONTENT[sectionId];
  const navItem = NAV.find(n=>n.id===sectionId);
  const { items: liveItems, loading: drillsLoading } = useDrills(sectionId);
  const { watched, markWatched, toggleWatched } = useWatched();
  const handleWatchedThreshold = useCallback((item, pct) => {
    if (item?.id) markWatched(item.id, pct);
  }, [markWatched]);
  const handleToggleWatched = useCallback((item) => {
    if (item?.id) toggleWatched(item.id);
  }, [toggleWatched]);
  // The big player. Held here rather than in the card so it is one
  // layer over the page, wherever the click came from, and so closing
  // it puts you back on the grid you were already looking at.
  const [theaterItem, setTheaterItem] = useState(null);
  const handleOpenTheater = useCallback((item) => {
    if (!item?.muxPlaybackId) return;
    setTheaterItem(item);
    try { logEvent("video_theater_open", { content_id: item.id, title: item.title, kind: sectionId }); } catch {}
  }, [sectionId]);
  const closeTheater = useCallback(() => setTheaterItem(null), []);
  // Changing section while the theater is open would leave it playing
  // over a page it has nothing to do with.
  useEffect(() => { setTheaterItem(null); }, [sectionId]);

  // Deep-link from dashboard bookmarks: FavCard stashes {sectionId, id}
  // in sessionStorage then navigates here. Open the item once the drill
  // list has hydrated (live items arrive async, so this re-runs as they
  // land). Mirrors openDetail's module/countdown branching — openDetail
  // itself is defined after the plans early-return, so we can't call it
  // from a hook that must run unconditionally.
  useEffect(() => {
    let target = null;
    try { target = JSON.parse(sessionStorage.getItem("bam_open_drill") || "null"); } catch {}
    if (!target || target.sectionId !== sectionId) return;
    const items = liveItems.length > 0 ? liveItems : (section?.items || []);
    const item = items.find(i => i.id === target.id || String(i.id) === String(target.id));
    if (!item) {
      // Stale bookmark (item deleted/renamed): once loading settles with
      // no match, drop the key so it can't fire on a later visit.
      if (!drillsLoading && items.length > 0) {
        try { sessionStorage.removeItem("bam_open_drill"); } catch {}
      }
      return;
    }
    try { sessionStorage.removeItem("bam_open_drill"); } catch {}
    if (item.type === "module" && item.moduleKey && MODULES[item.moduleKey]) {
      if (isLocked(item)) {
        setCountdownItem(item);
      } else {
        openModule(item.moduleKey, item.id);
      }
    } else if (isLocked(item)) {
      // A teased drill reached by deep link gets the takeover too,
      // otherwise a bookmark or a shared link walks straight past the
      // thing the greying is there to promise.
      setCountdownItem(item);
    } else {
      setSelectedItem(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, liveItems, drillsLoading]);

  // NOTE: the "plans" branch returns lower down, AFTER all hooks have run,
  // so no hook is ever called conditionally (react-hooks/rules-of-hooks).
  // Use live data if available, else static fallback
  // While Supabase drills are still loading, do NOT fall back to the
  // static mock data. Otherwise the page paints with the full static
  // list (often dozens of items) for ~200ms, then re-renders with the
  // real (often filtered) live data — a jarring "showed up like this
  // at first then loaded the right videos" flash. We'd rather show
  // the skeleton during that brief window.
  const sectionItemsBase = liveItems.length > 0
    ? liveItems
    : (drillsLoading ? [] : (section?.items || []));
  const sectionItems = sectionItemsBase.filter(notShelved);

  // Prompt 1: save search to recents
  const commitSearch=(term)=>{
    const t=term.trim();
    if(!t) return;
    const updated=[t,...recentSearches.filter(s=>s!==t)].slice(0,5);
    setRecentSearches(updated);
    try{localStorage.setItem("bam_recent_searches",JSON.stringify(updated));}catch{}
    setSearchFocused(false);
  };

  // Sections with `filterSource: "categories"` (Team Coaching) match
  // against the curated drills.categories text[] array. Filter ids stay
  // kebab-case in state and on chip clicks; display swaps to a friendly
  // label via TEAM_CATEGORY_LABEL.
  const useCategories = section.filterSource === "categories";
  // Collections (Pro Development) group by which library the video lives
  // in rather than by skill, so the chips match item.section.
  const useSections = section.filterSource === "section";
  // Format ids ("on-air", "ssg") always render via DRILL_FORMAT_LABEL
  // regardless of useCategories — keeps the chip readable as "On-Air
  // Drill" / "Small-Sided Game" instead of the raw id.
  const filterLabel = (id) => {
    if (DRILL_FORMAT_LABEL[id]) return DRILL_FORMAT_LABEL[id];
    if (useSections) return PRO_KIND_LABEL[id] || id;
    return useCategories ? (TEAM_CATEGORY_LABEL[id] || id) : id;
  };
  const itemMatchesCategoryFilter = (item, filterId) =>
    Array.isArray(item.categories) && item.categories.includes(filterId);

  // Prompt 2: filter stacking - group filters by classification
  const filterGroups = {};
  section.filters.filter(f=>f!=="All").forEach(f=>{
    const matchesLevel = !useCategories && !useSections && sectionItems.some(i=>i.level===f);
    const group = matchesLevel ? "level" : "tag";
    const tagLabel = useSections ? "Kind" : "Category";
    if(!filterGroups[group]) filterGroups[group]={label:group==="level"?"Level":tagLabel,filters:[]};
    filterGroups[group].filters.push(f);
  });
  // Format axis — separate chip group when the section opts in via
  // `formats: [...]` in navigation.js. Matches against item.format
  // (drills.format on the SQL side). AND-stacks with the level + tag
  // groups; OR-stacks within itself.
  const formatChips = Array.isArray(section.formats) ? section.formats : [];
  if (formatChips.length > 0) {
    filterGroups.format = {
      label: "Format",
      filters: formatChips,
    };
  }
  const formatChipSet = new Set(formatChips);

  const toggleFilter=(f)=>{
    if(f==="All"){setActiveFilters(["All"]);return;}
    setActiveFilters(prev=>{
      const without=prev.filter(x=>x!=="All"&&x!==f);
      const has=prev.includes(f);
      if(has){return without.length===0?["All"]:without;}
      return [...without,f];
    });
  };

  // Prompt 2: AND between groups, OR within group
  // Items may store the category on `category` (drill library) or `tag` (masterclasses).
  const itemTag = (i) => i.category || i.tag;
  const activeNonAll = activeFilters.filter(f=>f!=="All");
  // Pull format chips out first so they don't get mis-bucketed as
  // category/level filters by the "matches some item" heuristic.
  const activeFormatFilters = activeNonAll.filter(f => formatChipSet.has(f));
  const activeNonFormat = activeNonAll.filter(f => !formatChipSet.has(f));
  const activeLevelFilters = (useCategories || useSections)
    ? []
    : activeNonFormat.filter(f=>sectionItems.some(i=>i.level===f));
  const activeTagFilters = (useCategories || useSections)
    ? activeNonFormat
    : activeNonFormat.filter(f=> f==="Guest" || (sectionItems.some(i=>itemTag(i)===f)&&!sectionItems.some(i=>i.level===f)));

  let filtered = sectionItems;
  // Bookmarks tab - filter to only items saved by this coach for this section.
  if (viewTab === "bookmarks") {
    const bookmarkedIds = new Set(
      useSections
        ? favDrills.map(f => f.id)
        : favDrills.filter(f => f.sectionId === sectionId).map(f => f.id)
    );
    filtered = filtered.filter(item => bookmarkedIds.has(item.id));
  }
  // Coming Soon tab — pre-release items: a dated one (availableAt in the
  // future) or a dateless `comingSoon` one (no set release date yet).
  // Visible whenever the section has any pre-release items (currently
  // only matters for masterclasses).
  if (viewTab === "coming") {
    const now = Date.now();
    filtered = filtered.filter(item => item.comingSoon || (item.availableAt && new Date(item.availableAt).getTime() > now));
  }
  // Compute source counts (before source-filter is applied) and source filter.
  // Items without a coachId default to BAM until the Supabase backfill stamps
  // a real coach — the count math has to match the filter rule below or the
  // "(N)" badge undercounts and shows "BAM videos (0)" while 121 items are
  // actually visible.
  //
  // While the coaches table is still loading, isBamCoach() returns false
  // for every coachId (because byId is empty), which previously made the
  // BAM filter only pass items WITHOUT a coachId — typically just a
  // handful of legacy/mock rows. Symptoms: "BAM videos (21)" → 30s
  // later → "BAM videos (605)". Two guards while coaches.loading:
  //   • bamCount falls back to allCount so the badge doesn't show a
  //     wrong, narrower number that pops up a moment later.
  //   • the BAM filter passes everything through (treat all items as
  //     BAM until we know otherwise) so cards aren't transiently hidden.
  const coachesLoading = !!coaches?.loading;
  const isBamCoach = (coachId) => {
    const c = coaches?.byId?.[coachId];
    return c && (c.type === "staff" || c.type === "masterclass");
  };
  const allCount = filtered.length;
  const bamCount = coachesLoading
    ? allCount
    : filtered.filter(item => !item.coachId || isBamCoach(item.coachId)).length;
  if (source === "bam" && !coachesLoading) {
    filtered = filtered.filter(item => !item.coachId || isBamCoach(item.coachId));
    // Items without a coach are treated as BAM until proven otherwise -
    // keeps old mockData content visible while the Supabase backfill
    // propagates.
  }
  // "More filters" - coach multi-select.
  if (selectedCoachIds.size > 0) {
    filtered = filtered.filter(item => selectedCoachIds.has(item.coachId));
  }
  if(activeNonAll.length>0){
    filtered = filtered.filter(item=>{
      const passLevel = activeLevelFilters.length===0 || activeLevelFilters.includes(item.level);
      // Curated taxonomy: chip ids match against item.categories[]; OR
      // semantics so a drill tagged transition+defense passes either.
      const passTag = activeTagFilters.length===0
        ? true
        : useSections
          ? activeTagFilters.includes(item.section)
          : useCategories
            ? activeTagFilters.some(f => itemMatchesCategoryFilter(item, f))
            : activeTagFilters.some(f => f==="Guest" ? !!item.guest : itemTag(item)===f);
      // Format axis — strict match against item.format. Items without a
      // format (legacy rows that haven't been tagged yet) are excluded
      // when any format chip is active; that's intentional so coaches
      // know they're seeing the curated subset.
      const passFormat = activeFormatFilters.length===0
        ? true
        : activeFormatFilters.includes(item.format);
      return passLevel && passTag && passFormat;
    });
  }

  // Prompt 1: search filtering
  // Capture the post-chip / pre-search set so the AI semantic results
  // below can intersect against it (respecting category / level / bam /
  // bookmark filters but ignoring the substring match itself).
  const prefiltered = filtered;
  const trimQ = searchQ.trim().toLowerCase();
  const isSearching = trimQ.length > 0;
  if(isSearching){
    const has = (s) => typeof s === "string" && s.toLowerCase().includes(trimQ);
    filtered = filtered.filter(item =>
      has(item.title) || has(item.sub) || has(item.tag) || has(item.category) || has(item.desc)
    );
  }

  // AI-only matches: drills the embedder surfaced that the substring
  // filter missed. We rebuild from the pre-search set so chip filters
  // still apply, then drop anything already in `filtered` so the same
  // card never renders twice. Order preserved from the API response
  // (similarity desc).
  const exactIds = new Set(filtered.map(i => i.id));
  const prefilteredById = new Map(prefiltered.map(i => [i.id, i]));
  // Carry the API similarity score through onto the local card object so
  // ContentCard can render the relevance pill / sort indicator without
  // re-fetching. Spread keeps the local-shape fields (thumb, etc.).
  const aiOnly = isSearching
    ? aiResults
        .filter(r => !exactIds.has(r.id) && prefilteredById.has(r.id))
        .map(r => ({ ...prefilteredById.get(r.id), aiSimilarity: r.similarity }))
    : [];

  // Teased drills come out of the grid and into their own strip above
  // it, so a coach meets them once as a deliberate row rather than
  // finding dim cards scattered through the watchable ones.
  //
  // Only in the plain library view: the Coming Soon tab exists to list
  // them all at full strength, a search should still find them, and
  // masterclasses already have their own pre-release shelf.
  const stripActive = sectionId !== "master" && viewTab === "library" && !isSearching;
  const teasedItems = stripActive ? filtered.filter(isPreRelease) : [];
  if (teasedItems.length > 0) filtered = filtered.filter(i => !isPreRelease(i));

  // Re-order for today. Not while searching - a search has a right
  // answer and relevance is it - and not on the bookmarks tab, where
  // the coach built the order themselves by saving things.
  //
  // The seed is the date, so the grid holds still all day and turns
  // over tomorrow. See constants/shuffle.js for why that beats both
  // per-render and per-visit randomness.
  const shuffleActive = !isSearching && viewTab === "library" && sectionId !== "master";
  if (shuffleActive) filtered = shuffleDaily(filtered);

  // Watched sinks. Same idea as the teased strip in reverse: out of the
  // grid, into a shelf of its own at the bottom, dimmed. Nothing is
  // hidden - a coach can still see everything they have been through,
  // it just stops occupying the space where new things should be.
  //
  // Off during search for the same reason as the shuffle: if you typed
  // the name of a drill you watched last week, you want the drill.
  const sinkWatched = !isSearching && viewTab === "library" && watched.size > 0;
  const watchedItems = sinkWatched ? filtered.filter(i => watched.has(i.id)) : [];
  if (watchedItems.length > 0) filtered = filtered.filter(i => !watched.has(i.id));

  // New rises, and stays up until you have watched it.
  //
  // The shuffle on its own buried fresh uploads: the order used to be
  // created_at desc, so anything new was at the top by default, and
  // scattering the grid quietly took that away. New content is the
  // reason a coach comes back, so it gets held above the shuffle
  // rather than thrown into it.
  //
  // "Until seen" is read as "until watched" deliberately. Tracking
  // whether a card crossed the viewport sounds closer to the words,
  // but it clears on a fast scroll past, and a rail you can empty
  // without looking at anything is worse than no rail.
  //
  // Two limits, because neither alone is enough. The window stops a
  // drill a coach never opens from sitting up there forever. The cap
  // stops a three-hundred-video upload from becoming the whole page -
  // and the pool is shuffled on the same daily seed before it is
  // capped, so which twelve you meet rotates rather than the same
  // twelve waiting every day until you watch them.
  const NEW_SHELF_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
  const NEW_SHELF_CAP = 12;
  const isFresh = (i) => {
    const raw = i.createdAt || i.date_added;
    if (!raw) return false;
    const t = new Date(raw).getTime();
    return !Number.isNaN(t) && t <= Date.now() && (Date.now() - t) <= NEW_SHELF_WINDOW_MS;
  };
  const newShelfActive = shuffleActive && activeNonAll.length === 0;
  const freshPool = newShelfActive ? filtered.filter(isFresh) : [];
  const newShelfItems = freshPool.slice(0, NEW_SHELF_CAP);
  if (newShelfItems.length > 0) {
    const lifted = new Set(newShelfItems.map(i => i.id));
    filtered = filtered.filter(i => !lifted.has(i.id));
  }

  // Reset the native render window whenever the filtered set changes
  // (section switch, search, chip filter) so we start from the top again.
  const _filterSig = filtered.length + ":" + (filtered[0]?.id ?? "") + ":" + trimQ;
  useEffect(() => { setVisibleCount(24); }, [_filterSig]);

  // Infinite scroll: grow the window as the sentinel nears the viewport.
  // Runs on web too (not just native) — rendering the full unwindowed list
  // (hundreds of drills w/ thumbnails) crashes mobile Safari out of memory.
  // Deps are ONLY _filterSig, never visibleCount. The callback grows
  // visibleCount via a functional updater, so it needs nothing from the
  // closure. If visibleCount were a dep, every grow would disconnect and
  // recreate the observer, and the fresh observe() re-reports the still
  // on-screen sentinel, cascading setVisibleCount until React throws
  // "Maximum update depth exceeded" (crashes on any mid-size filtered
  // list). A stable observer fires once per real intersection change and
  // re-arms cleanly on the next scroll. _filterSig re-observes the new
  // sentinel when the filtered set changes.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisibleCount((c) => c + 24);
    }, { rootMargin: "500px" });
    io.observe(el);
    return () => io.disconnect();
  }, [_filterSig]);

  // Plans section renders the full builder. Returned here, after every hook
  // above has run, so taking this branch never skips a hook.
  if (sectionId === "plans") return (
    <Suspense fallback={<LazyPlansFallback/>}>
      <PracticePlansPage C={C} dark={dark} focusMode={planFocusMode} setFocusMode={setPlanFocusMode} favDrills={favDrills} favLists={favLists} favListMembership={favListMembership}/>
    </Suspense>
  );

  // Prompt 5: scroll position memory
  const scrollKey = `bam_scroll_${sectionId}`;
  const openDetail=(item)=>{
    // Masterclass modules open a full-screen reader instead of the side panel.
    if (item?.type === "module" && item.moduleKey && MODULES[item.moduleKey]) {
      // Pre-release modules land on the countdown view, not the reader.
      // Allowlisted reviewers (Coleman + team) bypass the gate so they
      // can preview the slides in production. Once availableAt passes,
      // the gate clears for everyone and the reader opens normally.
      if (isLocked(item)) {
        setCountdownItem(item);
        logEvent("masterclass_countdown_open", { moduleKey: item.moduleKey, itemId: item.id, availableAt: item.availableAt });
        return;
      }
      openModule(item.moduleKey, item.id);
      return;
    }
    // Teased drill: the takeover, not the video. Without this the card
    // dims and carries a COMING SOON pill and then plays anyway.
    if (isLocked(item)) {
      setCountdownItem(item);
      logEvent("tease_open", { itemId: item.id, section: sectionId, availableAt: item.availableAt || null });
      return;
    }
    if(scrollContainerRef.current){
      try{sessionStorage.setItem(scrollKey,""+scrollContainerRef.current.scrollTop);}catch{}
    }
    setSelectedItem(item);
  };
  const closeDetail=()=>{
    setSelectedItem(null);
    setTimeout(()=>{
      if(scrollContainerRef.current){
        try{const pos=parseInt(sessionStorage.getItem(scrollKey)||"0",10);scrollContainerRef.current.scrollTop=pos;}catch{}
      }
    },0);
  };

  // Identity-stable card handlers so the memoized ContentCard grid doesn't
  // re-render on every search keystroke (inline closures defeat React.memo).
  const openDetailRef = useRef(null); openDetailRef.current = openDetail;
  const handleCardOpen = useCallback((it) => { openDetailRef.current?.(it); }, []);
  const toggleSelectedRef = useRef(null); toggleSelectedRef.current = toggleSelected;
  const handleCardBulk = useCallback((it) => { toggleSelectedRef.current?.(it.id); }, []);

  // Masterclasses get a two-section layout in the default (unfiltered) view:
  // "New This Week" (just the new ones), then "All Masterclasses" (the full
  // catalog). Other sections keep their single-grid layout.
  // "New This Week" is date-driven so a lingering isNew flag can't keep old
  // content in the rail forever: an item counts as new only if it's flagged
  // isNew AND was added within the last 7 days. Stale flags expire on their
  // own, no manual cleanup needed.
  const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const isNewThisWeek = (i) => {
    if (!i.isNew || !i.date_added) return false;
    const t = new Date(i.date_added).getTime();
    return !Number.isNaN(t) && t <= Date.now() && (Date.now() - t) <= NEW_WINDOW_MS;
  };
  const newItems = (!isSearching && activeNonAll.length === 0) ? filtered.filter(isNewThisWeek) : [];
  const splitNew = sectionId === "master" && newItems.length > 0;

  // Pro Development splits long-form off the top: a coach landing here
  // wants to see how a whole session runs before they want one rep, and
  // a full workout buried among sixty drills never gets found.
  // Only when both shelves have something on them - one shelf under a
  // heading that promises two is worse than no heading at all.
  const longItems = (sectionId === PRO_COLLECTION && !isSearching && activeNonAll.length === 0)
    ? filtered.filter(isLongVideo) : [];
  const shortItems = longItems.length > 0 ? filtered.filter(i => !isLongVideo(i)) : [];
  const splitLong = longItems.length > 0 && shortItems.length > 0;
  const renderItemGrid = (items, keyPrefix = "") => (
    <div className="bam-drill-grid bam-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16}}>
      {items.slice(0, visibleCount).map(item=>(
        <div key={`${keyPrefix}${item.id}`} style={{position:"relative"}}>
          <ContentCard item={item} sectionId={sectionId} C={C} dark={dark}
            onClick={bulkTagActive ? handleCardBulk : handleCardOpen}
            favDrills={favDrills} toggleFav={toggleFav} masterProgress={masterProgress}
            /* Collections, from the grid. `toggleFavInList` is keyed on the
               bookmark key, so a drill that isn't saved has to be saved
               first: picking a collection is the whole gesture. */
            favLists={favLists}
            memberLists={favListMembership[`${sectionId}-${item.id}`] || []}
            rosterNames={rosterNames}
            onToggleList={toggleFavInList ? (listId) => {
              const key = `${sectionId}-${item.id}`;
              if (!favDrills.some(f => f.key === key)) toggleFav?.(item, sectionId);
              toggleFavInList(key, listId);
            } : undefined}
            onCreateList={createFavList ? (name) => {
              const key = `${sectionId}-${item.id}`;
              if (!favDrills.some(f => f.key === key)) toggleFav?.(item, sectionId);
              const id = createFavList(name);
              if (id) toggleFavInList?.(key, id);
            } : undefined}
            dim={viewTab === "library"}
            watched={watched.has(item.id)}
            onWatchedThreshold={handleWatchedThreshold}
            onToggleWatched={bulkTagActive ? undefined : handleToggleWatched}
            onOpenTheater={bulkTagActive ? undefined : handleOpenTheater}/>
          {bulkTagActive && (
            <AdminSelectOverlay selected={selectedIds.has(item.id)} onToggle={() => toggleSelected(item.id)} C={C}/>
          )}
          {bulkTagActive && item.type && (
            <div style={{position:"absolute",bottom:52,left:10,fontSize:9,fontWeight:800,color:GOLD,background:"rgba(0,0,0,0.7)",border:`1px solid ${GOLD}55`,padding:"2px 7px",borderRadius:4,letterSpacing:.5,zIndex:3,pointerEvents:"none"}}>{item.type.toUpperCase()}</div>
          )}
        </div>
      ))}
    </div>
  );

  // Masterclasses: shelves by category instead of one flat grid with chips
  // doing the sorting. Position carries the category, so the card doesn't
  // have to. Pre-release items sit in their own row at the bottom rather
  // than dimmed among the live ones. With a search or a chip active the
  // shelves collapse to one grid of whatever matched.
  // Coaching Concepts leads. It is the widest door: the ideas apply whoever
  // you coach, where the other two shelves assume you already know which
  // half of the job you are shopping for.
  const MASTER_SHELVES = ["Coaching Concepts", "Player Development", "Team Coaching"];
  const mcTag = (i) => i.tag || i.category || "";
  const mcPre = isPreRelease;
  // One card, so the shelves and the New panel cannot drift apart in how a
  // masterclass looks. Only the LAYOUT around them differs.
  const masterCard = (item, dimPre = false) => (
    <div style={{position:"relative"}}>
      <MasterclassCard item={item} C={C} sectionId={sectionId}
        mod={MODULES[item.moduleKey] || null}
        onClick={bulkTagActive ? handleCardBulk : handleCardOpen}
        favDrills={favDrills} toggleFav={toggleFav} masterProgress={masterProgress}
        dim={dimPre}/>
      {bulkTagActive && (
        <AdminSelectOverlay selected={selectedIds.has(item.id)} onToggle={() => toggleSelected(item.id)} C={C}/>
      )}
    </div>
  );
  const renderMasterGrid = (items, keyPrefix = "", dimPre = false) => (
    <div className="bam-drill-grid bam-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:18}}>
      {items.map(item => (
        <React.Fragment key={`${keyPrefix}${item.id}`}>{masterCard(item, dimPre)}</React.Fragment>
      ))}
    </div>
  );
  const shelfHeader = (label, count) => (
    <div style={{display:"flex",alignItems:"baseline",gap:10,margin:"0 0 14px"}}>
      <span style={{fontFamily:"'Bebas Neue', sans-serif",fontSize:22,letterSpacing:1.6,color:C.text,lineHeight:1}}>{label}</span>
      {count > 0 && <span style={{fontSize:11,color:C.textDim,fontVariantNumeric:"tabular-nums"}}>{count}</span>}
      <div style={{flex:1,height:1,background:C.border,alignSelf:"center"}}/>
    </div>
  );
  const renderMasterclasses = () => {
    const shelved = viewTab === "library" && !isSearching && activeNonAll.length === 0;
    if (!shelved) return renderMasterGrid(filtered);
    const live = filtered.filter(i => !mcPre(i));
    const soon = filtered.filter(mcPre);
    // Just landed, by date rather than the hand-set `isNew` flag. A fresh
    // item shows on the New shelf INSTEAD of its category one, so no card
    // appears twice. See constants/newShelf.js.
    const { fresh, settled } = splitFresh(live, NEW_SHELF_DAYS);
    const known = new Set(MASTER_SHELVES);
    const shelves = MASTER_SHELVES
      .map(label => [label, settled.filter(i => mcTag(i) === label)])
      .filter(([, items]) => items.length > 0);
    const other = settled.filter(i => !known.has(mcTag(i)));
    if (other.length) shelves.push(["More", other]);
    return (
      <>
        <NewShelf items={fresh} C={C} dark={dark} days={NEW_SHELF_DAYS}
          renderCard={(item) => masterCard(item)}/>
        {shelves.map(([label, items], idx) => (
          <div key={label} style={{marginTop: (idx === 0 && fresh.length === 0) ? 0 : 34}}>
            {shelfHeader(label, items.length)}
            {renderMasterGrid(items, `${label}-`)}
          </div>
        ))}
        {soon.length > 0 && (
          <div style={{marginTop:34}}>
            {shelfHeader("Coming soon", soon.length)}
            {renderMasterGrid(soon, "soon-", true)}
          </div>
        )}
        {shelves.length === 0 && soon.length === 0 && (
          <div style={{padding:"40px 0",color:C.textDim,fontSize:13}}>Nothing here yet.</div>
        )}
      </>
    );
  };

  return (
    <div style={{display:"flex",flex:1,overflow:"hidden",height:"100%"}}>
      <div ref={scrollContainerRef} className="bam-content-scroll" style={{flex:1,overflow:"auto",padding:28}}>
        {/* Header — hidden on phones via .bam-content-header class. The
            topbar already shows the section label ("Drills & Games"),
            so a giant H1 + paragraph here just duplicates information
            and pushes the actual search + drills grid below the fold.
            Desktop keeps the header for context. */}
        <div className="bam-content-header" style={{marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
            {(() => { const HeadIcon = navItem?.Icon || section.Icon; return HeadIcon ? <HeadIcon size={20} color={GOLD} strokeWidth={2}/> : null; })()}
            <div style={{fontSize:20,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.5}}>{section.title}</div>
          </div>
          <div style={{fontSize:14,color:C.textMid,lineHeight:1.5}}>{section.desc}</div>
        </div>

        {/* The NBA player development article, pinned to the top of the pro
            library (Coleman, 2026-10-07): it is the written companion to the
            pro clips, and it is deliberately not on the masterclass shelf.
            Members read it in the app's own reader, not the public page,
            which would hand a paying coach a sign-up button. */}
        {sectionId === PRO_COLLECTION && (
          <button type="button" className="bam-pro-article"
            onClick={() => openModule(NBA_PLAYER_DEV_KEY, `pro-${NBA_PLAYER_DEV_KEY}`)}
            style={{display:"flex",alignItems:"stretch",width:"100%",padding:0,marginBottom:22,
              borderRadius:14,overflow:"hidden",cursor:"pointer",textAlign:"left",fontFamily:"inherit",
              border:`1px solid ${C.border}`,background:dark?"#141414":"#FFFFFF",
              boxShadow:dark?"0 10px 30px rgba(0,0,0,0.35)":"0 4px 16px rgba(0,0,0,0.06)"}}>
            <div className="bam-pro-article-art" style={{position:"relative",flexShrink:0,overflow:"hidden",background:"#000"}}>
              <img src={muxFrame(NBA_PLAYER_DEV_MODULE.coverVideoId, { width: 640, height: 360 })} alt="" aria-hidden="true"
                style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
            </div>
            <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",justifyContent:"center",gap:7,padding:"18px 22px"}}>
              <div style={{fontSize:10.5,fontWeight:800,letterSpacing:1.5,textTransform:"uppercase",color:C.textMid}}>
                Article{NBA_PLAYER_DEV_MODULE.duration ? ` · ${NBA_PLAYER_DEV_MODULE.duration}` : ""}
              </div>
              <div style={{fontSize:20,fontWeight:800,color:C.text,lineHeight:1.2,letterSpacing:-.3}}>{NBA_PLAYER_DEV_MODULE.title}</div>
              {NBA_PLAYER_DEV_MODULE.sub && (
                <div style={{fontSize:13.5,color:C.textMid,lineHeight:1.45}}>{NBA_PLAYER_DEV_MODULE.sub}</div>
              )}
              <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:4,fontSize:12.5,fontWeight:800,color:C.text}}>
                Read it <ArrowRight size={14} strokeWidth={2.4}/>
              </div>
            </div>
          </button>
        )}

        {/* The two halves of Insights. They were separate sidebar items with
            the same label, one under Player Development and one under Team
            Coaching, and nothing connected them: a coach reading a PD insight
            had no idea the team one existed. One section now, and this is the
            way across. Both routes stay real, so every link ever sent still
            lands. */}
        {(sectionId === "insights" || sectionId === "ti") && (
          <div className="bam-chip-row" style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap"}}>
            {[["insights","Player Development"],["ti","Team"]].map(([id,label])=>{
              const on = sectionId === id;
              return (
                <button key={id} type="button" className="btn btn-inline"
                  onClick={()=>{ if(!on) window.location.hash = `#/${id}`; }}
                  aria-current={on ? "page" : undefined}
                  style={{padding:"8px 16px",borderRadius:9,fontSize:12.5,fontWeight:800,letterSpacing:.2,
                    cursor:on?"default":"pointer",fontFamily:"inherit",
                    border:`1px solid ${on?GOLD:C.border}`,background:on?`${GOLD}18`:"transparent",
                    color:on?GOLD:C.textMid,transition:"all .15s"}}>
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Lessons rail — appears above the existing content cards on
            the Insights surfaces. Surface-specific via audience filter
            (pd-tagged on insights, team-tagged on ti, both-tagged on
            both). Cards click into MasterclassReader. */}
        {(sectionId === "insights" || sectionId === "ti") && (() => {
          const audience = sectionId === "ti" ? "team" : "pd";
          const lessons = lessonsForAudience(audience);
          if (lessons.length === 0) return null;
          return (
            <LessonsRail
              lessons={lessons}
              C={C}
              dark={dark}
              onOpen={(lessonKey) => {
                const masterItem = sectionItems.find(it => it.moduleKey === lessonKey);
                openModule(lessonKey, masterItem?.id ?? `lesson-${lessonKey}`);
              }}
            />
          );
        })()}

        {/* Library / Bookmarks top toggle + BAM/All source toggle */}
        {(()=>{
          // On a collection page the bookmark was saved against the
          // drill's own section, so count what's actually on this page
          // rather than what was saved under this page's id.
          const sectionBookmarkCount = useSections
            ? sectionItems.filter(i => favDrills.some(f => f.id === i.id)).length
            : favDrills.filter(f => f.sectionId === sectionId).length;
          // Pre-release count for the Coming Soon tab — only show the
          // tab if there are actually items waiting to drop, so it
          // doesn't clutter sections that don't use availableAt.
          const nowTs = Date.now();
          const comingSoonCount = sectionItems.filter(i => i.comingSoon || (i.availableAt && new Date(i.availableAt).getTime() > nowTs)).length;
          // Source toggle only matters for sections where multiple sources
          // make sense - drill grids and insights. Hide on masterclasses,
          // plans, etc.
          // Source toggle hidden when the BAM and All counts match —
          // there's no choice to offer when every drill is from a BAM
          // coach. Loading-state guard treats the toggle as "match" so
          // we don't flash a transient redundant chip before coaches
          // table hydrates.
          const countsMatch = !coachesLoading && bamCount === allCount;
          const showSourceToggle = ["pd","team","insights","ti","xo","movement","workouts"].includes(sectionId) && !countsMatch;
          const tabs = [
            {id:"library",  label:"Library"},
            {id:"bookmarks",label:`Bookmarks${sectionBookmarkCount?` (${sectionBookmarkCount})`:""}`},
          ];
          if (comingSoonCount > 0) {
            tabs.push({id:"coming", label:`Coming Soon (${comingSoonCount})`});
          }
          return (
            <div className="bam-chip-row bam-drill-tabs" style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{display:"flex",gap:6,position:"relative"}}>
                {/* The gold pill slides to the chosen tab (shared/motion). */}
                <Glide variant="pill"/>
                {tabs.map(t => {
                  const on = viewTab === t.id;
                  return (
                    <button key={t.id} onClick={()=>setViewTab(t.id)} data-active={on}
                      className="bam-section-tab"
                      style={{padding:"8px 18px",borderRadius:9,fontSize:12,fontWeight:800,letterSpacing:.3,cursor:"pointer",
                        border:`1px solid ${on?GOLD:C.border}`,
                        background:on?`${GOLD}18`:"transparent",
                        color:on?GOLD:C.textMid,transition:"all .15s"}}>
                      {t.label}
                    </button>
                  );
                })}
              </div>
              {showSourceToggle && (
                <VideoSourceToggle value={source} onChange={setSource} C={C}
                  counts={{ bam: bamCount, all: allCount }}/>
              )}
            </div>
          );
        })()}

        {/* Search bar */}
        <div style={{position:"relative",marginBottom:18}}>
          <div style={{display:"flex",alignItems:"center",gap:9,background:dark?"rgba(255,255,255,0.03)":C.bgHover,border:`1px solid ${searchFocused?"rgba(255,255,255,0.18)":C.cardBorder||C.border}`,borderRadius:10,padding:"8px 14px",transition:"border-color .15s ease"}}>
            <Ic.Search c={GOLD} s={14}/>
            <input ref={searchInputRef} value={searchQ}
              onChange={e=>setSearchQ(e.target.value)}
              onFocus={()=>setSearchFocused(true)}
              onBlur={()=>setTimeout(()=>setSearchFocused(false),200)}
              onKeyDown={e=>{if(e.key==="Enter"&&searchQ.trim())commitSearch(searchQ);}}
              placeholder={SEMANTIC_SECTIONS.has(sectionId) ? (typedHint || "Search or describe a drill…") : "Search content..."}
              style={{flex:1,background:"transparent",border:"none",outline:"none",fontSize:13,color:C.text,fontFamily:"'DM Sans',sans-serif"}}/>
            {searchQ && (
              <button onClick={()=>{setSearchQ("");setSearchFocused(false);}}
                aria-label="Clear search"
                style={{display:"flex",alignItems:"center",justifyContent:"center",width:36,height:36,padding:0,
                  background:dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)",border:"none",borderRadius:"50%",
                  cursor:"pointer",color:C.textDim,transition:"background .15s ease, color .15s ease"}}
                onMouseEnter={e=>{e.currentTarget.style.background=dark?"rgba(255,255,255,0.16)":"rgba(0,0,0,0.12)";e.currentTarget.style.color=C.text;}}
                onMouseLeave={e=>{e.currentTarget.style.background=dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.06)";e.currentTarget.style.color=C.textDim;}}>
                <X size={13} strokeWidth={2.5}/>
              </button>
            )}
          </div>
          {/* Recent searches dropdown */}
          {searchFocused&&!searchQ&&recentSearches.length>0&&(
            <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:20,marginTop:4,background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:10,padding:"8px 0",boxShadow:dark?"0 12px 40px rgba(0,0,0,0.6)":`0 8px 24px ${C.shadow}`}}>
              <div style={{fontSize:10,fontWeight:700,color:C.textDim,padding:"4px 14px",letterSpacing:.8,textTransform:"uppercase"}}>Recent Searches</div>
              {recentSearches.map((s,i)=>(
                <div key={i} className="btn" onMouseDown={e=>{e.preventDefault();setSearchQ(s);commitSearch(s);}}
                  style={{padding:"7px 14px",fontSize:13,color:C.textMid,cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                  <Clock size={11} color={C.textDim}/>{s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filters — chip rows scroll horizontally on phones so 5-7 filter
            options stay on a single line instead of wrapping to 2-3 rows.
            "All" sits inline with the category chips on mobile. */}
        <div style={{marginBottom:18}}>
          <div className="bam-chip-row bam-drill-filters" style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap",alignItems:"center"}}>
            <div className="tag-pill" onClick={()=>toggleFilter("All")}
              style={{display:"inline-flex",alignItems:"center",fontSize:12,fontWeight:700,padding:"6px 16px",borderRadius:20,border:`1px solid ${activeFilters.includes("All")?GOLD:C.border}`,background:activeFilters.includes("All")?GOLD:"transparent",color:activeFilters.includes("All")?"#111":C.textMid,transition:"background .15s, color .15s, border-color .15s",whiteSpace:"nowrap"}}>
              All
            </div>
            {/* Pro Development doorway. Sits with the filters because that's
                where a coach is already looking, but it is deliberately
                styled apart from them (filled, gold, arrow) so nobody
                clicks it expecting the list below to narrow. The hover
                asks the question the one-word label can't. */}
            {/* Collections: Pro and Movement, doorways rather than filters
                (Coleman, 2026-10-07: Movement moved out of the sidebar to sit
                with Pro). Labelled and filled gold, with an arrow, so nobody
                clicks one expecting the list below to narrow. */}
            {sectionId==="pd" && (
              <span className="bam-chip-row-label" style={{fontSize:10,fontWeight:700,color:C.textDim,letterSpacing:.8,textTransform:"uppercase",alignSelf:"center",marginLeft:4}}>Collections:</span>
            )}
            {sectionId==="pd" && proCollection && (
              <div className="tag-pill bam-pro-chip" onClick={enterPro}
                role="button" tabIndex={0}
                onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); enterPro(); } }}
                aria-label={`${proCollection.chipLabel}. ${proCollection.hint}`}
                data-hint={proCollection.hint}
                style={{fontSize:12,fontWeight:800,padding:"6px 14px",borderRadius:20,
                  border:`1px solid ${GOLD}`,background:`${GOLD}1A`,color:GOLD,
                  display:"inline-flex",alignItems:"center",gap:6,whiteSpace:"nowrap",
                  cursor:"pointer",transition:"background .15s, transform .15s"}}>
                {proCollection.chipLabel}<ArrowRight size={12}/>
              </div>
            )}
            {sectionId==="pd" && (
              <div className="tag-pill bam-pro-chip" onClick={enterMovement}
                role="button" tabIndex={0}
                onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); enterMovement(); } }}
                aria-label="Movement Drills. Warmups, plyometrics, coordination, mobility, physicality."
                data-hint="Warmups, plyometrics, coordination, mobility, physicality."
                style={{fontSize:12,fontWeight:800,padding:"6px 14px",borderRadius:20,
                  border:`1px solid ${GOLD}`,background:`${GOLD}1A`,color:GOLD,
                  display:"inline-flex",alignItems:"center",gap:6,whiteSpace:"nowrap",
                  cursor:"pointer",transition:"background .15s, transform .15s"}}>
                Movement<ArrowRight size={12}/>
              </div>
            )}
            {Object.entries(filterGroups).map(([gKey,g])=>(
              <div key={gKey}
                /* Format chips collapse into the More-filters sheet on
                   phones — they're a secondary axis (drill vs. SSG) that
                   most coaches don't pivot on every session. Category
                   stays visible. Class drives the CSS hide rule. */
                className={`bam-filter-group bam-filter-group-${gKey}`}
                style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <span className="bam-chip-row-label" style={{fontSize:9,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginRight:2}}>{g.label}:</span>
                {g.filters.map(f=>(
                  <div key={f} className="tag-pill" onClick={()=>toggleFilter(f)}
                    style={{fontSize:12,fontWeight:700,padding:"6px 16px",borderRadius:20,border:`1px solid ${activeFilters.includes(f)?GOLD:C.border}`,background:activeFilters.includes(f)?GOLD:"transparent",color:activeFilters.includes(f)?"#111":C.textMid,transition:"background .15s, color .15s, border-color .15s",whiteSpace:"nowrap"}}>
                    {filterLabel(f)}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {activeNonAll.length>0&&(
            <div className="bam-content-header bam-showing-line" style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.textMid,marginBottom:4}}>
              <span style={{fontWeight:700}}>Showing:</span>
              {activeLevelFilters.length>0&&<span>{activeLevelFilters.join(" or ")}</span>}
              {activeLevelFilters.length>0&&activeTagFilters.length>0&&<span>·</span>}
              {activeTagFilters.length>0&&<span>{activeTagFilters.map(filterLabel).join(" or ")}</span>}
              {(activeLevelFilters.length>0||activeTagFilters.length>0)&&activeFormatFilters.length>0&&<span>·</span>}
              {activeFormatFilters.length>0&&<span>{activeFormatFilters.map(filterLabel).join(" or ")}</span>}
              <span className="btn" onClick={()=>setActiveFilters(["All"])} style={{color:C.textDim,fontSize:13,marginLeft:4,cursor:"pointer"}}>×</span>
            </div>
          )}
        </div>

        {/* More filters - collapsible coach filter + future extensions */}
        {["pd","team","insights","ti","xo","movement","workouts"].includes(sectionId) && (
          <MoreFilters C={C} dark={dark} coaches={coaches}
            selectedCoachIds={selectedCoachIds}
            setSelectedCoachIds={setSelectedCoachIds}/>
        )}

        {/* New badge row — for masterclasses this heads the "New This Week"
            section above "All Masterclasses"; other sections keep the simple
            header above their single grid. */}
        {(sectionId!=="master" && !isSearching && filtered.some(isNewThisWeek))&&(
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:16}}>
            <span style={{fontSize:16}}>⚡</span>
            <span style={{fontSize:15,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.5}}>New This Week</span>
            <div style={{flex:1,height:1,background:C.border}}/>
          </div>
        )}

        {/* Team Insights: promo card for the AAU masterclass */}
        {sectionId==="ti"&&!isSearching&&activeNonAll.length===0&&(
          <div className="btn" onClick={()=>{
            try{sessionStorage.setItem("bam_open_module",AAU_MODULE_KEY);}catch{}
            logEvent("masterclass_promo_click",{from:"ti",moduleKey:AAU_MODULE_KEY});
            window.location.hash="#/master";
          }}
            style={{display:"flex",alignItems:"stretch",gap:16,marginBottom:22,padding:"18px 20px",borderRadius:14,
              background:`linear-gradient(135deg, ${GOLD}15 0%, ${GOLD}05 100%)`,
              border:`1px solid ${GOLD}55`,cursor:"pointer",position:"relative",overflow:"hidden"}}>
            <div style={{flexShrink:0,width:44,height:44,borderRadius:10,background:`${GOLD}22`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <GraduationCap size={22} color={GOLD}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",marginBottom:4}}>New Masterclass</div>
              <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:4,lineHeight:1.3}}>The Development-First AAU Model</div>
              <div style={{fontSize:12.5,color:C.textMid,lineHeight:1.5}}>An 8-chapter interactive guide for running a development-first club or AAU program - with 6 film clips and a program audit.</div>
            </div>
            <div style={{flexShrink:0,alignSelf:"center",display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:800,color:GOLD}}>
              Open <ArrowRight size={14}/>
            </div>
          </div>
        )}

        {/* Coming soon — teased drills, three previews and a count. */}
        {teasedItems.length > 0 && (
          <ComingSoonStrip items={teasedItems} sectionId={sectionId} C={C} dark={dark}
            onOpen={handleCardOpen} favDrills={favDrills} toggleFav={toggleFav}/>
        )}

        {/* Grid */}
        {drillsLoading && sectionItems.length === 0 ? (
          <ContentGridSkeleton count={8} poster={sectionId === "master"}/>
        ) : (
        <>
          {/* When the user is searching, label the exact-substring grid
              so the AI-only section below reads as a separate bucket. */}
          {isSearching && filtered.length > 0 && (
            <div style={{display:"flex",alignItems:"center",gap:8,margin:"4px 0 10px"}}>
              <span style={{fontSize:10,fontWeight:800,letterSpacing:1.2,textTransform:"uppercase",color:C.textDim}}>Exact matches</span>
              <span style={{fontSize:10,fontWeight:700,color:C.textDim}}>({filtered.length})</span>
            </div>
          )}
          {newShelfItems.length > 0 && (
            <>
              <div style={{display:"flex",alignItems:"center",gap:9,margin:"0 0 16px"}}>
                <span style={{fontSize:15,fontWeight:800,color:GOLD,textTransform:"uppercase",letterSpacing:.5}}>New</span>
                {freshPool.length > newShelfItems.length && (
                  <span style={{fontSize:11,fontWeight:700,color:C.textDim,fontVariantNumeric:"tabular-nums"}}>
                    {newShelfItems.length} of {freshPool.length}
                  </span>
                )}
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              {renderItemGrid(newShelfItems, "new-shelf-")}
              {/* When the Longer videos split is also running it brings
                  its own heading, so a second divider here would just be
                  a rule under a rule. */}
              {!splitLong && (
                <div style={{display:"flex",alignItems:"center",gap:9,margin:"30px 0 16px"}}>
                  <span style={{fontSize:15,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.5}}>Everything else</span>
                  <div style={{flex:1,height:1,background:C.border}}/>
                </div>
              )}
              {splitLong && <div style={{height:14}}/>}
            </>
          )}
          {sectionId === "master" ? renderMasterclasses() : splitLong ? (
            <>
              <div style={{display:"flex",alignItems:"center",gap:9,margin:"0 0 16px"}}>
                <span style={{fontSize:15,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.5}}>Longer videos</span>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              {renderItemGrid(longItems, "long-")}
              <div style={{display:"flex",alignItems:"center",gap:9,margin:"30px 0 16px"}}>
                <span style={{fontSize:15,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.5}}>Everything else</span>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              {renderItemGrid(shortItems, "short-")}
            </>
          ) : splitNew ? (
            <>
              {renderItemGrid(newItems, "new-")}
              <div style={{display:"flex",alignItems:"center",gap:9,margin:"30px 0 16px"}}>
                <span style={{fontSize:15,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.5}}>All {section.title || "Masterclasses"}</span>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              {renderItemGrid(filtered, "all-")}
            </>
          ) : (
            renderItemGrid(filtered)
          )}
          {watchedItems.length > 0 && (
            <>
              <div style={{display:"flex",alignItems:"center",gap:9,margin:"34px 0 16px"}}>
                <span style={{fontSize:15,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.5}}>Watched</span>
                <span style={{fontSize:11,fontWeight:700,color:C.textDim,fontVariantNumeric:"tabular-nums"}}>{watchedItems.length}</span>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              {renderItemGrid(watchedItems, "watched-")}
            </>
          )}
          {sectionId !== "master" && visibleCount < filtered.length && (
            <div ref={loadMoreRef} style={{padding:"20px 0",textAlign:"center",fontSize:11,color:C.textDim,letterSpacing:0.5,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <span aria-hidden style={{width:13,height:13,borderRadius:"50%",border:`2px solid ${GOLD}33`,borderTopColor:GOLD,display:"inline-block",animation:"bamLoadSpinCP .8s linear infinite"}}/>
              <style>{`@keyframes bamLoadSpinCP { to { transform: rotate(360deg); } }`}</style>
              Loading more… ({filtered.length - visibleCount} left)
            </div>
          )}

          {/* Smart-matches section. Renders when the user is searching
              and the embedder surfaced drills the substring filter
              missed. When there are zero exact matches, this bucket *is*
              the result — relabel it "Best matches" so it doesn't feel
              like a sidecar suggestion. */}
          {isSearching && aiOnly.length > 0 && (
            <>
              <div style={{display:"flex",alignItems:"center",gap:8,margin: filtered.length === 0 ? "4px 0 10px" : "24px 0 10px"}}>
                <Sparkles size={12} color={GOLD}/>
                <span style={{fontSize:10,fontWeight:800,letterSpacing:1.2,textTransform:"uppercase",color:GOLD}}>{filtered.length === 0 ? "Best matches" : "Smart matches"}</span>
                <span style={{fontSize:10,fontWeight:700,color:C.textDim}}>({aiOnly.length})</span>
                <span style={{fontSize:10,color:C.textDim,fontStyle:"italic",marginLeft:4}}>matched by concept, not keyword</span>
              </div>
              <div className="bam-drill-grid bam-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16}}>
                {aiOnly.map(item=>(
                  <div key={`ai-${item.id}`} style={{position:"relative"}}>
                    <ContentCard item={item} sectionId={sectionId} C={C} dark={dark}
                      onClick={bulkTagActive ? handleCardBulk : handleCardOpen}
                      favDrills={favDrills} toggleFav={toggleFav} masterProgress={masterProgress}
                      isAiMatch={true}/>
                    {bulkTagActive && (
                      <AdminSelectOverlay
                        selected={selectedIds.has(item.id)}
                        onToggle={() => toggleSelected(item.id)}
                        C={C}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* AI bucket "no smart matches" affordance. Only shown when
              exact returned hits but the embedder didn't — signals that
              the system tried so users don't assume the feature broke. */}
          {isSearching && !aiLoading && filtered.length > 0 && aiOnly.length === 0
            && SEMANTIC_SECTIONS.has(sectionId) && trimQ.length >= 3 && (
            <div style={{display:"flex",alignItems:"center",gap:8,margin:"24px 0 0",fontSize:11,color:C.textDim}}>
              <Sparkles size={11} color={C.textDim} style={{opacity:0.5}}/>
 <span>No smart matches: try fewer or different words.</span>
            </div>
          )}

          {/* Loading shimmer for the AI section while the debounced
              fetch is in flight and we don't yet have results. Keeps the
              user oriented when the exact-match list is short or empty. */}
          {isSearching && aiLoading && aiOnly.length === 0 && (
            <div style={{display:"flex",alignItems:"center",gap:8,margin:"20px 0 0",fontSize:11,color:C.textDim}}>
              <Sparkles size={11} color={GOLD} style={{opacity:0.6}}/>
              <span>Looking for smart matches…</span>
            </div>
          )}
        </>
        )}

        {/* Empty states */}
        {filtered.length===0&&aiOnly.length===0&&!aiLoading&&isSearching&&(
          <div style={{textAlign:"center",padding:"60px 0",color:C.textDim}}>
            <img src={LOGO} alt="BAM" className="micro-pulse" style={{width:48,height:56,objectFit:"contain",margin:"0 auto 16px",display:"block",opacity:.45,filter:`drop-shadow(0 0 12px ${GOLD}33)`}}/>
            <div style={{fontSize:15,fontWeight:600,marginBottom:20}}>Nothing found - search again with a different idea!</div>
            <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"18px 20px",maxWidth:420,margin:"0 auto",textAlign:"left"}}>
              <div style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:.8,textTransform:"uppercase",marginBottom:10}}>Smart Match Hints</div>
              <div style={{fontSize:13,color:C.textMid,lineHeight:1.7}}>
                {trimQ.includes("finish")&&<div style={{marginBottom:6}}>Try: "1v1 Live Finishing" - a constraint drill for creative finishing</div>}
                {trimQ.includes("shoot")&&<div style={{marginBottom:6}}>Try: "Catch & Shoot Progression" - systematic shooting series</div>}
                {!trimQ.includes("finish")&&!trimQ.includes("shoot")&&(
                  <>
                    <div style={{marginBottom:6}}>Try searching for a specific skill like "finishing" or "handles"</div>
                    <div style={{marginBottom:6}}>Or browse by filter tags above</div>
                    <div>Try broader terms like "drill", "game", or "decision"</div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {filtered.length===0&&!isSearching&&(
          <div style={{textAlign:"center",padding:"60px 0",color:C.textDim}}>
            <Filter size={32} color={C.textDim} style={{marginBottom:12,opacity:.4}}/>
            <div style={{fontSize:15,fontWeight:600}}>No content with this filter yet.</div>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedItem&&(
        <ContentDetail item={selectedItem} sectionId={sectionId} C={C} dark={dark} onClose={closeDetail} masterProgress={masterProgress} setMasterProgress={setMasterProgress} videoNotes={videoNotes} saveVideoNotes={saveVideoNotes} onWatchedThreshold={handleWatchedThreshold} onOpenTheater={handleOpenTheater}
          favDrills={favDrills} toggleFav={toggleFav}
          favLists={favLists} rosterNames={rosterNames}
          memberLists={favListMembership[`${sectionId}-${selectedItem.id}`] || []}
          onToggleList={toggleFavInList ? (listId) => toggleFavInList(`${sectionId}-${selectedItem.id}`, listId) : undefined}
          onCreateList={createFavList ? (name) => {
            const id = createFavList(name);
            if (id) toggleFavInList?.(`${sectionId}-${selectedItem.id}`, id);
          } : undefined}/>
      )}

      {/* The big player. Desktop only - the component checks the
          viewport itself, and below it the callers play in place. */}
      {theaterItem && (
        <VideoTheater item={theaterItem} C={C} dark={dark} onClose={closeTheater}
          onWatchedThreshold={handleWatchedThreshold}
          watched={watched.has(theaterItem.id)}
          onToggleWatched={handleToggleWatched}/>
      )}

      {/* Full-screen masterclass reader */}
      {openModuleKey && MODULES[openModuleKey] && (()=>{
        const mod = MODULES[openModuleKey];
        // Progress is saved per shelf item. The fallback used to be 9001,
        // which is a real item (the AAU masterclass), so a lesson, or
        // anything opened outside the shelf, wrote its progress into AAU's
        // and the home page offered "Finish The Development-First AAU
        // Model" to people who had never opened it. Look the item up on
        // the masterclass shelf, and failing that key it by the module.
        const masterItem = sectionItems.find(it => it.moduleKey === openModuleKey)
          || (CONTENT.master?.items || []).find(it => it.moduleKey === openModuleKey);
        const itemId = masterItem?.id ?? `module-${openModuleKey}`;
        return (
          <MasterclassReader
            module={mod}
            C={C}
            dark={dark}
            onClose={closeModule}
            onProgress={handleModuleProgress(itemId)}
          />
        );
      })()}

      {/* Countdown view for pre-release modules. Shows the cover, title,
          description, and a live-ticking countdown. Auto-flips into the
          reader the moment the timer hits zero (no refresh needed). */}
      {countdownItem && (
        <MasterclassCountdown
          /* Drills have no `thumb`; give the takeover a real frame off
             Mux so a teased drill isn't a black rectangle. */
          item={countdownItem.thumb || !countdownItem.muxPlaybackId
            ? countdownItem
            : { ...countdownItem, thumb: `https://image.mux.com/${countdownItem.muxPlaybackId}/thumbnail.jpg?width=1280&height=720&fit_mode=smartcrop&time=8` }}
          module={MODULES[countdownItem.moduleKey] || null}
          C={C}
          dark={dark}
          onClose={() => setCountdownItem(null)}
          onUnlock={() => {
            const it = countdownItem;
            setCountdownItem(null);
            // The clock ran out while they were watching it. A module
            // opens its reader; a drill opens the normal detail panel.
            if (it.moduleKey && MODULES[it.moduleKey]) openModule(it.moduleKey, it.id);
            else setSelectedItem(it);
          }}
        />
      )}

      {/* Admin bulk-tag bar (pd / team only, admin mode only) */}
      {bulkTagActive && (
        <DrillBulkBar
          selected={selectedIds}
          onClearSelection={clearSelection}
          onSaved={() => window.location.reload()}
          categories={section?.filters?.filter(f => f !== "All") || []}
          C={C}
          dark={dark}
        />
      )}
    </div>
  );
}
